/**
 * LiveKit room management for live streaming.
 * Mirrors mobile LiveLiveKitService — separate from callStore Room.
 */
import { Room, Track } from 'livekit-client'
import { LIVE_TIMEOUTS } from '../constants/liveConfig'
import { buildNativeAspectLiveKitVideoOptions } from '../utils/liveVideoLayout'

let _room = null
let _listener = null
let _disconnectOp = null
let _cameraFacing = 'user'

// Remote audio playback. LiveKit does NOT auto-play remote audio in plain JS —
// each subscribed audio track must be attached to an <audio> element or there is
// no sound (the <video> element only carries the video track). We attach every
// remote audio track (host + guests + OBS) here. `_audioMuted` lets a silent
// surface (the OBS confidence monitor) suppress playback without affecting the
// viewer experience.
const _audioByTrack = new Map() // RemoteAudioTrack -> HTMLAudioElement
let _audioMuted = false

function _attachAudio(track) {
  if (_audioByTrack.has(track)) return
  const el = track.attach() // creates an <audio>, begins autoplay
  el.muted = _audioMuted
  el.setAttribute('data-live-audio', '')
  el.style.display = 'none'
  // Some browsers only play media elements that are in the DOM.
  document.body.appendChild(el)
  _audioByTrack.set(track, el)
}

function _detachAudio(track) {
  const el = _audioByTrack.get(track)
  if (!el) return
  try { track.detach(el) } catch { /* */ }
  el.remove()
  _audioByTrack.delete(track)
}

function _detachAllAudio() {
  for (const [track, el] of _audioByTrack) {
    try { track.detach(el) } catch { /* */ }
    el.remove()
  }
  _audioByTrack.clear()
}

/**
 * Room options for the web broadcaster.
 *
 * Quality decisions (see deep analysis in the OBS/live notes):
 * - Capture targets 1080p, native aspect (see buildNativeAspectLiveKitVideoOptions).
 * - `maxBitrate: 4.5 Mbps` matches LiveKit's 1080p reference — 3 Mbps starved 1080p
 *   into a blocky picture. 720p captures simply use less of this ceiling.
 * - **No `videoSimulcastLayers`**: omitting them lets the SDK auto-generate an
 *   orientation-aware 3-rung ladder (~1080p/540p/270p) scaled from the REAL
 *   capture. The old hardcoded landscape `h180`/`h360` overrode that with a low,
 *   landscape-only ladder that was wrong for portrait captures.
 * - `degradationPreference: 'maintain-framerate'`: under CPU/upload pressure drop
 *   resolution, not motion — keeps the stream smooth and stable, and weak
 *   broadcasters auto-fall to 720p/540p instead of freezing.
 * - `videoCodec: 'h264'` kept: Android viewers decode H.264 in hardware (VP8 churns).
 */
function _buildRoomOptions() {
  return {
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: buildNativeAspectLiveKitVideoOptions(_cameraFacing),
    publishDefaults: {
      simulcast: true,
      videoCodec: 'h264',
      videoEncoding: {
        maxBitrate: 4_500_000,
        maxFramerate: 30,
      },
      degradationPreference: 'maintain-framerate',
    },
  }
}

const _handlers = {
  disconnected: new Set(),
  reconnecting: new Set(),
  reconnected: new Set(),
  trackSubscribed: new Set(),
  trackUnsubscribed: new Set(),
  localTrackPublished: new Set(),
  // Fired with `canPlaybackAudio` when the browser blocks/allows autoplay so the
  // UI can show a "tap for sound" prompt.
  audioPlaybackChanged: new Set(),
}

function _emit(set, ...args) {
  set.forEach((fn) => {
    try { fn(...args) } catch { /* */ }
  })
}

function _attachListeners() {
  _detachListeners()
  if (!_room) return
  const r = _room
  const onDisconnected = (reason) => _emit(_handlers.disconnected, reason)
  const onReconnecting = () => _emit(_handlers.reconnecting)
  const onReconnected = () => _emit(_handlers.reconnected)
  const onTrackSubscribed = (track, publication, participant) => {
    // Play remote audio — without this attach there is no sound at all.
    if (track.kind === Track.Kind.Audio) _attachAudio(track)
    _emit(_handlers.trackSubscribed, { track, publication, participant })
  }
  const onTrackUnsubscribed = (track, publication, participant) => {
    if (track.kind === Track.Kind.Audio) _detachAudio(track)
    _emit(_handlers.trackUnsubscribed, { track, publication, participant })
  }
  const onLocalTrackPublished = (publication) => {
    _emit(_handlers.localTrackPublished, publication)
  }
  const onAudioPlaybackChanged = () => {
    _emit(_handlers.audioPlaybackChanged, r.canPlaybackAudio)
  }
  r.on('disconnected', onDisconnected)
  r.on('reconnecting', onReconnecting)
  r.on('reconnected', onReconnected)
  r.on('trackSubscribed', onTrackSubscribed)
  r.on('trackUnsubscribed', onTrackUnsubscribed)
  r.on('localTrackPublished', onLocalTrackPublished)
  r.on('audioPlaybackChanged', onAudioPlaybackChanged)
  _listener = {
    clear: () => {
      r.off('disconnected', onDisconnected)
      r.off('reconnecting', onReconnecting)
      r.off('reconnected', onReconnected)
      r.off('trackSubscribed', onTrackSubscribed)
      r.off('trackUnsubscribed', onTrackUnsubscribed)
      r.off('localTrackPublished', onLocalTrackPublished)
      r.off('audioPlaybackChanged', onAudioPlaybackChanged)
    },
  }
}

function _detachListeners() {
  _listener?.clear?.()
  _listener = null
}

export function onLiveKitEvent(event, handler) {
  const set = _handlers[event]
  if (!set) return () => {}
  set.add(handler)
  return () => set.delete(handler)
}

export function getLiveKitRoom() {
  return _room
}

export function isLiveKitConnected() {
  return _room?.state === 'connected'
}

export async function disconnectLiveKit() {
  const existing = _disconnectOp
  if (existing) {
    await existing
    return
  }
  const op = (async () => {
    _detachListeners()
    _detachAllAudio()
    try {
      await _room?.disconnect()
    } catch { /* */ }
    _room = null
  })()
  _disconnectOp = op
  try {
    await op
  } finally {
    _disconnectOp = null
  }
}

async function _connect(wsUrl, token, asHost, options = {}) {
  await disconnectLiveKit()

  // Each fresh session defaults to audible; a silent surface re-mutes after join.
  _audioMuted = false
  _room = new Room(_buildRoomOptions())
  _attachListeners()

  await Promise.race([
    _room.connect(wsUrl, token, { autoSubscribe: true }),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('LiveKit connection timed out')), LIVE_TIMEOUTS.livekitConnectionMs),
    ),
  ])

  if (asHost) {
    const audioOnly = options.audioOnly === true
    if (!audioOnly) {
      await _room.localParticipant.setCameraEnabled(true)
    }
    await _room.localParticipant.setMicrophoneEnabled(true)
  }

  // Prime audio playback inside the user gesture that triggered the join (a
  // click), satisfying browser autoplay policy. If still blocked, the room emits
  // `audioPlaybackChanged` → the UI shows a "tap for sound" prompt.
  try { await _room.startAudio() } catch { /* surfaced via audioPlaybackChanged */ }

  return _room
}

export async function connectLiveKitAsHost(wsUrl, token, options = {}) {
  return _connect(wsUrl, token, true, options)
}

export async function connectLiveKitAsViewer(wsUrl, token) {
  return _connect(wsUrl, token, false)
}

export async function setLiveKitCameraEnabled(enabled) {
  if (!_room?.localParticipant) return false
  await _room.localParticipant.setCameraEnabled(enabled)
  return true
}

export async function setLiveKitMicrophoneEnabled(enabled) {
  if (!_room?.localParticipant) return false
  await _room.localParticipant.setMicrophoneEnabled(enabled)
  return true
}

export async function switchLiveKitCamera() {
  _cameraFacing = _cameraFacing === 'user' ? 'environment' : 'user'
  const lp = _room?.localParticipant
  if (!lp) return
  const pub = lp.videoTrackPublications?.[0]
  const track = pub?.track
  if (track?.setCameraFacing) {
    await track.setCameraFacing(_cameraFacing === 'user' ? 'front' : 'back')
  } else {
    await lp.setCameraEnabled(false)
    await lp.setCameraEnabled(true)
  }
}

export function getLocalVideoTrack() {
  const lp = _room?.localParticipant
  if (!lp) return null
  for (const pub of lp.videoTrackPublications.values()) {
    if (pub.track) return pub.track
  }
  return null
}

function _cameraTrackForParticipant(participant) {
  if (!participant) return null
  for (const pub of participant.videoTrackPublications.values()) {
    if (pub.subscribed && pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera && pub.track) {
      return pub.track
    }
  }
  return null
}

// Any subscribed video track for a specific participant. Used for the host
// preference so OBS lives resolve even if the ingress publishes its video under
// a non-Camera source — the camera-source check alone could miss it and fall
// through to a guest's track for the main stage.
function _anyVideoTrackForParticipant(participant) {
  if (!participant) return null
  const camera = _cameraTrackForParticipant(participant)
  if (camera) return camera
  for (const pub of participant.videoTrackPublications.values()) {
    if (pub.subscribed && pub.kind === Track.Kind.Video && pub.track) return pub.track
  }
  return null
}

/**
 * The HOST's remote camera track (for the main viewer stage).
 *
 * When `preferredIdentity` is set — the stream owner's userId for camera lives,
 * or `obs:<liveStreamId>` for OBS lives — that participant's camera is preferred
 * over everyone else's. Without this, a guest who joins can win the "first remote
 * camera" race and the main stage would show the guest instead of the host.
 * Falls back to the first remote camera, then any subscribed track.
 */
export function getRemoteVideoTrack(preferredIdentity = null) {
  if (!_room) return null
  if (preferredIdentity) {
    const preferred = _anyVideoTrackForParticipant(_room.remoteParticipants.get(preferredIdentity))
    if (preferred) return preferred
  }
  for (const participant of _room.remoteParticipants.values()) {
    const track = _cameraTrackForParticipant(participant)
    if (track) return track
  }
  for (const participant of _room.remoteParticipants.values()) {
    for (const pub of participant.videoTrackPublications.values()) {
      if (pub.subscribed && pub.track) return pub.track
    }
  }
  return null
}

/** A specific remote participant's camera track (one guest tile). */
export function getRemoteParticipantVideoTrack(identity) {
  if (!identity || !_room) return null
  return _cameraTrackForParticipant(_room.remoteParticipants.get(identity))
}

/**
 * Remote guest camera tracks keyed by participant identity (== userId),
 * excluding `excludeIdentity` (the host / OBS ingress). Lets the guest stage
 * map each track to the matching `activeGuests[i]` entry.
 */
export function getRemoteGuestVideoTrackMap(excludeIdentity = null) {
  const map = {}
  if (!_room) return map
  for (const participant of _room.remoteParticipants.values()) {
    if (excludeIdentity && participant.identity === excludeIdentity) continue
    const track = _cameraTrackForParticipant(participant)
    if (track) map[participant.identity] = track
  }
  return map
}

/**
 * Display identity for a remote participant, parsed from the LiveKit metadata the
 * backend embeds in the guest token (`displayName` / `username` /
 * `profilePictureUrl`). Lets a tile label itself from LiveKit alone when the
 * `live:guest:joined` socket event was missed during renegotiation churn.
 */
export function getRemoteParticipantMeta(identity) {
  const raw = _room?.remoteParticipants.get(identity)?.metadata
  if (!raw) return null
  try {
    const decoded = JSON.parse(raw)
    if (!decoded || typeof decoded !== 'object') return null
    const field = (key) => {
      const value = decoded[key]
      return typeof value === 'string' && value.trim() ? value.trim() : null
    }
    return {
      displayName: field('displayName'),
      username: field('username'),
      profilePictureUrl: field('profilePictureUrl'),
    }
  } catch {
    return null
  }
}

export function attachTrackToElement(track, el) {
  if (!track || !el) return () => {}
  track.attach(el)
  return () => {
    try { track.detach(el) } catch { /* */ }
  }
}

/**
 * Resume audio playback after a user gesture (autoplay policy). Returns true if
 * playback is now permitted. The UI calls this from a tap/click handler.
 */
export async function startLiveKitAudio() {
  if (!_room) return false
  try {
    await _room.startAudio()
  } catch { /* */ }
  return _room.canPlaybackAudio
}

/** Whether the browser currently allows audio playback (false = needs a gesture). */
export function isLiveKitAudioAllowed() {
  return _room ? _room.canPlaybackAudio : true
}

/**
 * Mute/unmute ALL remote audio for this session without unsubscribing. Used by
 * the OBS confidence monitor to stay silent (avoids echo/feedback with the
 * broadcaster's own delayed audio) while viewers keep full sound.
 */
export function setLiveKitAudioMuted(muted) {
  _audioMuted = muted
  for (const el of _audioByTrack.values()) el.muted = muted
}

export function getCameraFacing() {
  return _cameraFacing
}

export function setCameraFacing(facing) {
  _cameraFacing = facing
}
